import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import api from 'api';

export const checkEmailKeys = {
    all: ['checkEmail'] as const,
    email: (email: string) => ['checkEmail', email] as const,
};

export interface CheckEmailResult {
    /** true when the API accepts the email (not already registered). */
    available: boolean;
}

const normalizeEmail = (email: string) => email.trim().toLowerCase();

/** Debounce a value (e.g. search input) before firing requests. */
export function useDebouncedValue<T>(value: T, delayMs = 400): T {
    const [debounced, setDebounced] = useState(value);

    useEffect(() => {
        const timer = window.setTimeout(() => setDebounced(value), delayMs);
        return () => window.clearTimeout(timer);
    }, [value, delayMs]);

    return debounced;
}

/**
 * Shared query function for GET /check_email/:email.
 * Success → available; error → already in use (legacy behaviour).
 */
export async function fetchEmailAvailability(email: string): Promise<CheckEmailResult> {
    const normalized = normalizeEmail(email);
    try {
        await api.checkEmail.get(normalized);
        return { available: true };
    } catch {
        return { available: false };
    }
}

/**
 * Returns an async validator suitable for Yup `.test()` / Formik validation.
 * Uses react-query's fetchQuery so results are cached across blur/submit.
 *
 * @returns `true` when the email is free, `false` when already taken
 */
export function useCheckEmailAvailability() {
    const queryClient = useQueryClient();

    return useCallback(
        async (email: string): Promise<boolean> => {
            const normalized = normalizeEmail(email);
            if (!normalized) {
                return true;
            }

            const result = await queryClient.fetchQuery({
                queryKey: checkEmailKeys.email(normalized),
                queryFn: () => fetchEmailAvailability(normalized),
                staleTime: 5 * 60 * 1000,
                gcTime: 10 * 60 * 1000,
                retry: false,
            });

            return result.available;
        },
        [queryClient]
    );
}

export default useCheckEmailAvailability;
